import React, { useCallback, useContext, useMemo } from 'react'
import { useIntl, FormattedMessage } from 'react-intl'

import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalFooter from 'LuxKit/Modal/ModalFooter'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import BodyText from 'LuxKit/Typography/BodyText'
import { ModalElementContext } from 'contexts/ModalProvider'
import noop from 'lib/function/noop'
import type { DeleteTripError } from 'tripPlanner/api/types'
import { useDeleteTrip } from 'tripPlanner/hooks/api/trip'
import useModalCallbacks from 'tripPlanner/hooks/View/useModalCallbacks'
import type { BasicTrip, FullTrip } from 'tripPlanner/types/common'
import config from 'constants/config'

interface Props {
  trip: BasicTrip | FullTrip
  onDelete?: () => void
}

function DeleteTrip({ trip, onDelete = noop }: Props) {
  const { id: tripId, name } = trip
  const intl = useIntl()

  const deleteTripTitle = intl.formatMessage({
    id: 'YJUfMbCG2WYrXuO2bg45',
    defaultMessage: 'Delete trip',
    description: 'Title for the delete trip confirmation modal in the trip planner',
  })

  const yesDeleteTripLabel = intl.formatMessage({
    id: 'D-adhIAkAhBZ8nfxH6_J',
    defaultMessage: 'Yes, delete trip',
    description: 'Confirm button label to permanently delete a trip in the trip planner item editor modal',
  })

  const cancelLabel = intl.formatMessage({
    id: 'nJWVAmQarU2JUXOzwsqX',
    defaultMessage: 'Cancel',
    description: 'Cancel button label in the delete trip modal in the trip planner',
  })

  const { closeModal, goBack } = useModalCallbacks()

  const modalElement = useContext(ModalElementContext)

  const onCloseModalClicked = useCallback(() => {
    closeModal()
    modalElement?.resolve()
  }, [closeModal, modalElement])

  const { mutate, isLoading } = useDeleteTrip({
    onSuccess: () => {
      onCloseModalClicked()
      onDelete()
    },
    onError: (e: DeleteTripError) => {
      if (e.status === 404) {
        throw new Error('Delete failed. Trip not found.')
      }
    },
  })

  const permanentDeleteWarning = useMemo(() => {
    const synonym = config.brandConfig.purchaseSynonym
    if (synonym === 'booking') {
      return intl.formatMessage({
        id: 'gzKDybZi2YERCSaIIf1u',
        defaultMessage: 'This action cannot be undone and will permanently delete your trip. Existing bookings will not be affected and can still be accessed in My Bookings.',
        description: 'Warning that trip deletion is permanent but existing bookings remain accessible in My Bookings',
      })
    }
    if (synonym === 'escape') {
      return intl.formatMessage({
        id: 'v51fEpGHMfIe0ThroXIl',
        defaultMessage: 'This action cannot be undone and will permanently delete your trip. Existing bookings will not be affected and can still be accessed in My Escapes.',
        description: 'Warning that trip deletion is permanent but existing bookings remain accessible in My Escapes',
      })
    }
    return intl.formatMessage({
      id: 'ZH_PT6rcDhjx5HUrGDIr',
      defaultMessage: 'This action cannot be undone and will permanently delete your trip. Existing bookings will not be affected and can still be accessed in My Purchases.',
      description: 'Warning that trip deletion is permanent but existing bookings remain accessible in My Purchases',
    })
  }, [intl])

  const deleteTrip = useCallback(() => {
    mutate(tripId)
  }, [mutate, tripId])

  return (
    <>
      <ModalHeader
        title={deleteTripTitle}
        onBackButtonClick={goBack}
        dismissible={false}
      />
      <ModalBody>
        <ModalContent>
          <BodyText variant="large" colour="neutral-one" as="p">
            <FormattedMessage
              id="xFGGhhLKxIBgzVp8XwMY"
              defaultMessage="Are you sure you want to delete <bold>{tripName}</bold>?"
              description="Confirmation question when user is about to delete a trip"
              values={{
                tripName: name,
                bold: (chunks) => <BodyText as="span" variant="large" weight="bold">{chunks}</BodyText>,
              }}
            />
          </BodyText>
          <BodyText variant="large" colour="neutral-one" as="p">
            {permanentDeleteWarning}
          </BodyText>
        </ModalContent>
      </ModalBody>
      <ModalFooter
        primaryActionProps={{
          children: yesDeleteTripLabel,
          disabled: isLoading,
          onClick: deleteTrip,
        }}
        secondaryActionProps={{
          children: cancelLabel,
          disabled: isLoading,
          onClick: onCloseModalClicked,
        }}
      />
    </>
  )
}

export default DeleteTrip
